import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../ui';
import { api } from '../../services/api.service';

interface LeaderboardEntry {
  rank?: number;
  username: string;
  userId: string;
  score: number;
  achievedAt: string;
}

interface LeaderboardProps {
  gameId: string;
  gameName?: string;
  limit?: number;
  showHeader?: boolean;
  compact?: boolean;
  highlightUserId?: string;
}

export function Leaderboard({
  gameId,
  gameName,
  limit = 10,
  showHeader = true,
  compact = false,
  highlightUserId,
}: LeaderboardProps) {
  const [scores, setScores] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const loadScores = useCallback(
    async (cursor?: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({ limit: limit.toString() });
        if (cursor) params.append('cursor', cursor);

        const response = await api.get<{
          scores: LeaderboardEntry[];
          nextCursor?: string;
        }>(`/highscores/${gameId}?${params}`);

        if (cursor) {
          setScores((prev) => [...prev, ...response.data.scores]);
        } else {
          setScores(response.data.scores);
        }
        setNextCursor(response.data.nextCursor || null);
      } catch (err) {
        setError('Failed to load leaderboard');
        console.error('Leaderboard error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [gameId, limit]
  );

  useEffect(() => {
    loadScores();
  }, [loadScores]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getRankDisplay = (rank: number | undefined, index: number) => {
    const displayRank = rank || index + 1;
    if (displayRank === 1) return '🥇';
    if (displayRank === 2) return '🥈';
    if (displayRank === 3) return '🥉';
    return `#${displayRank}`;
  };

  if (error) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-red-400 mb-4">{error}</p>
          <Button variant="outline" size="sm" onClick={() => loadScores()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card padding={compact ? 'sm' : 'md'}>
      {showHeader && (
        <CardHeader>
          <CardTitle className={compact ? 'text-lg' : ''}>
            {gameName ? `${gameName} Leaderboard` : 'Leaderboard'}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent>
        {isLoading && scores.length === 0 ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="h-10 bg-slate-700/50 rounded animate-pulse"
              />
            ))}
          </div>
        ) : scores.length === 0 ? (
          <p className="text-slate-400 text-center py-4">
            No scores yet. Be the first!
          </p>
        ) : (
          <>
            <div className="space-y-1">
              {scores.map((entry, index) => (
                <div
                  key={`${entry.userId}-${entry.score}`}
                  className={`
                    flex items-center gap-3 p-2 rounded-lg transition-colors
                    ${
                      highlightUserId === entry.userId
                        ? 'bg-purple-500/20 border border-purple-500/30'
                        : 'hover:bg-slate-700/30'
                    }
                  `}
                >
                  <span
                    className={`
                      w-8 text-center font-bold
                      ${index < 3 ? 'text-lg' : 'text-sm text-slate-400'}
                    `}
                  >
                    {getRankDisplay(entry.rank, index)}
                  </span>
                  <span className="flex-1 truncate text-white">
                    {entry.username}
                  </span>
                  <span className="font-mono text-purple-400">
                    {entry.score.toLocaleString()}
                  </span>
                  {!compact && (
                    <span className="text-xs text-slate-500 w-16 text-right">
                      {formatDate(entry.achievedAt)}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {nextCursor && (
              <Button
                variant="ghost"
                size="sm"
                fullWidth
                onClick={() => loadScores(nextCursor)}
                isLoading={isLoading}
                className="mt-4"
              >
                Load More
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
